import { forwardRef, useId, type ReactNode } from 'react'
import { Checkbox as AntCheckbox } from 'antd'
import type { CheckboxRef as AntCheckboxRef } from 'antd'
import './checkbox.css'

export interface CheckboxProps {
  checked?: boolean | string | null
  onChange?: (e: any) => void
  disabled?: boolean
  label?: ReactNode
  id?: string
  size?: number
  disabledBg?: string
  disabledColor?: string
  children?: ReactNode
  className?: string
  style?: React.CSSProperties
  name?: string
  indeterminate?: boolean
  crudtype?: string
  value?: any
  width?: string
  'aria-label'?: string
}

export const Checkbox = forwardRef<AntCheckboxRef, CheckboxProps>(function Checkbox(
  {
    checked,
    onChange,
    disabled,
    label,
    id: idProp,
    name,
    children,
    'aria-label': ariaLabel,
    ...rest
  },
  ref,
) {
  const fallbackId = useId()
  const id = idProp ?? fallbackId
  // If a visible label/children is rendered inside <AntCheckbox>, the
  // checkbox is its own implicit label; we still expose `aria-label` for
  // screen readers when neither is set.
  const accessibleName = ariaLabel ?? (typeof label === 'string' ? label : undefined)
  return (
    <div className='flex items-center gap-2'>
      <AntCheckbox
        ref={ref}
        className='glasspro-checkbox'
        checked={!!checked}
        onChange={onChange}
        disabled={disabled}
        id={id}
        name={name ?? id}
        aria-label={accessibleName}
        {...rest}
      >
        {label || children}
      </AntCheckbox>
    </div>
  )
})

// ---------------------------------------------------------------------------
// RoundedCheckbox
// ---------------------------------------------------------------------------
export interface RoundedCheckboxProps extends CheckboxProps {
  borderRadius?: number
}

export const RoundedCheckbox = forwardRef<AntCheckboxRef, RoundedCheckboxProps>(
  function RoundedCheckbox({ borderRadius = 50, ...props }, ref) {
    return (
      <div style={{ ['--checkbox-radius' as string]: `${borderRadius}%` }}>
        <Checkbox ref={ref} className='glasspro-checkbox-rounded' {...props} />
      </div>
    )
  },
)
